import type { Assets } from '../core/assets.js';
import type { Plane } from '../sim/sim.js';
import { FxRandom } from './particles.js';
export class ResultSequence {
  private scroll = 0;
  private counter = 0;
  private badgeX = 1024;
  private loserBadgeX = 0;
  private winnerPlaneX = 0;
  private loserPlaneX = 0;
  private loserDrop = 0;
  private winnerPanelX = 0;
  private loserPanelX = 0;
  private readonly clouds: {
    x: number;
    y: number;
  }[] = [];
  private readonly panels: [HTMLCanvasElement, HTMLCanvasElement];
  private readonly panelWidth: number;
  constructor(
    private readonly assets: Assets,
    private readonly winner: Plane,
    private readonly loser: Plane,
    private readonly rng: FxRandom,
  ) {
    for (let i = 0; i < 3; i++) this.clouds.push({ x: rng.below(128), y: 42 * i + rng.below(42) });
    this.panelWidth = assets.fontMain.measure(`${assets.strings.tg[11]} 100% `);
    this.panels = [winner, loser].map((p) => {
      const canvas = document.createElement('canvas');
      canvas.width = this.panelWidth;
      canvas.height = 15;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#fbfbfb';
      ctx.fillRect(0, 0, canvas.width, 15);
      ctx.fillStyle = '#474747';
      ctx.fillRect(0, 0, canvas.width, 1);
      ctx.fillRect(0, 14, canvas.width, 1);
      assets.fontMain.drawCentered(
        ctx,
        `${assets.strings.tg[11]}${p.accuracy}%`,
        canvas.width >> 1,
        3,
      );
      return canvas;
    }) as [HTMLCanvasElement, HTMLCanvasElement];
  }
  get done(): boolean {
    return this.counter > 350;
  }
  private image(
    ctx: CanvasRenderingContext2D,
    name: string,
    x: number,
    y: number,
    anchor: 3 | 10 | 20,
  ): void {
    const img = this.assets.sprites.get(name);
    if (!img) return;
    ctx.drawImage(
      img,
      x - (anchor === 10 ? img.width : anchor === 3 ? img.width >> 1 : 0),
      y - (anchor === 20 ? 0 : img.height >> 1),
    );
  }
  paint(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#b9dce0';
    ctx.fillRect(0, 0, 128, 128);
    for (let x = 0; x < 255 + this.scroll; x += 128) {
      this.image(ctx, 'misc_00', x - this.scroll, 124, 20);
      this.image(ctx, 'misc_01', x - this.scroll, 0, 20);
    }
    this.scroll += 2;
    if (this.scroll > 128) this.scroll = 0;
    for (let i = 2; i >= 0; i--) {
      const c = this.clouds[i]!;
      c.x -= 1 + i;
      this.image(ctx, 'cloud_00', c.x, c.y, 3);
      if (c.x < -32) {
        c.x = 160;
        c.y = 42 * i + this.rng.below(42);
      }
    }
    const badge = this.badgeX >> 6,
      loserBadge = this.loserBadgeX >> 6,
      winnerPlane = this.winnerPlaneX >> 6,
      loserPlane = this.loserPlaneX >> 6,
      drop = this.loserDrop >> 6;
    this.image(ctx, 'fx_64', badge, 20, 10);
    this.image(ctx, `fx_${this.winner.portrait + 8}`, badge - 25, 17, 10);
    const flicker = this.rng.below(3);
    this.image(ctx, 'fx_64', loserBadge, 80 + flicker + drop, 10);
    this.image(ctx, `fx_${this.loser.portrait + 8}`, loserBadge - 25, 77 + flicker + drop, 10);
    for (const [dx, y] of [
      [56, 75],
      [14, 85],
      [38, 93],
    ])
      this.image(ctx, `fx_${14 + this.rng.below(3)}`, loserBadge - dx!, y! + drop, 3);
    if (this.winnerPanelX - 32 - (this.panelWidth >> 1) < 64) this.winnerPanelX = winnerPlane;
    if (this.loserPanelX - 32 - (this.panelWidth >> 1) < 64) this.loserPanelX = loserPlane;
    this.carrier(ctx, this.winnerPlaneX >> 6, this.winnerPanelX, 50, this.panels[0], this.winner);
    this.carrier(ctx, this.loserPlaneX >> 6, this.loserPanelX, 110, this.panels[1], this.loser);
    this.counter++;
    if (this.badgeX >> 6 < 96 || this.counter > 300) this.badgeX += 50;
    if (this.counter > 70) this.winnerPlaneX += 120;
    if (this.counter > 100) {
      if (this.loserBadgeX >> 6 < 96 || this.counter > 300) this.loserBadgeX += 50;
      if (loserBadge > 96) this.loserDrop += 60;
    }
    if (this.counter > 170) this.loserPlaneX += 120;
  }
  private carrier(
    ctx: CanvasRenderingContext2D,
    x: number,
    panelX: number,
    y: number,
    panel: HTMLCanvasElement,
    p: Plane,
  ): void {
    this.image(ctx, 'fx_65', panelX - 24, y, 10);
    ctx.drawImage(panel, panelX - 32 - panel.width, y - (panel.height >> 1));
    this.image(ctx, 'fx_66', panelX - 32 - panel.width, y, 10);
    this.image(ctx, p.i === 3 ? 'plane_16' : 'fx_67', x, y, 10);
  }
}
